import { useQuery } from "@tanstack/react-query";
import { engineError } from "@/lib/engine-error";
import type { AggregateMonthToDate, MonthToDate } from "@/lib/profitability-api";
import { monthToDateKeys } from "@/lib/query";

/**
 * The open month's time-and-materials projects: margin so far, and where each
 * lands once the remaining guidance is worked.
 *
 * No paging and no server sort: the engine returns the whole month (~80 rows)
 * in one object, so the page sorts and filters what it holds. One minute
 * stale, matching the engine's own cache -- these are live hours.
 *
 * An Aggregate caller gets their book only, margin percent only
 * (`isAggregateMonthToDate`).
 */
export function useMonthToDate() {
  return useQuery({
    queryKey: monthToDateKeys.board,
    queryFn: async (): Promise<MonthToDate | AggregateMonthToDate> => {
      const response = await fetch("/api/month-to-date");
      if (!response.ok) {
        throw await engineError(response, "Could not load this month");
      }
      return response.json();
    },
    staleTime: 60 * 1000,
  });
}

/**
 * Which shape a month-to-date response is. Here rather than beside the types:
 * profitability-api.ts is server-side and must stay out of the browser bundle.
 */
export function isAggregateMonthToDate(
  data: MonthToDate | AggregateMonthToDate,
): data is AggregateMonthToDate {
  return "access" in data && data.access !== undefined;
}
